import {
    v2DeleteEmailProviderPath,
    v2GetCurrentUserPath,
    v2GetWorkspaceMembersPath,
    v2GetEmailProvidersPath,
    v2OnboardingSequencePath,
    v2UserGetZapierApiKeyPath,
    v2UserTagsPath,
    validateOnboardingPath,
    v2UserConnectSocialAccountPath,
    v2UserDisconnectSocialAccountPath,
    v2CreateCalendarItemPath,
    v2UpdateCalendarItemPath,
    v2UsersChangePasswordPath,
    v2UserUpdateUserEmailPath,
    v2UsersSendPasswordLinkPath,
    v2GetCalendarEventsOnDatePath,
    v2UserUpdatePhoneNumberForTwoFactorAuthenticationPath,
    v2UserConvertPreTrailerToTrailerPath
} from '~/utils/ts-routes.utils';
import {
    IAccessRequestResponse,
    IBookkeeperAccessResponse,
    IEmailDeliverabilityTestResponse,
    IFullstoryTokenResponse,
    IInviteBookkeeperDataForServer,
    IOnboardingSequence,
    IProAccessResponse,
    IUserResponse,
    IValidateEmailIntakeForm,
    IGoogleContactsResponse,
    ISaveContactRequest,
    ISaveContactResponse,
    IEditContactRequest,
    IGetNylasAuthURLRequest,
    IUpdateCalendarProviderRequest,
    IGetGoogleAuthURLRequest,
    SocialNetwork,
    ICreateEventWithClientResponseUser,
    IEmailProvidersResponse,
    TEmailProvider,
    ICalendarProvidersResponse,
    IZoomAuthProviderResponse,
    ICanvaAuthProviderResponse,
    IGetCalendarEventsOnDateResponse,
    IUpdate2FAStateResponseSuccess,
    IValidate2FACodeResponseSuccess,
    ISendLogin2FAResponseSuccess,
    IChangePasswordData,
    TChangePasswordResponse,
    TSendPasswordLinkResponse,
    TUpdatePhoneNumberForTwoFactorAuthResponse,
    IUpdatePhoneNumberForTwoFactorAuthData,
    ICampaign,
    TCampaignPage,
    IIntegratedCalendar,
    IUpdateCalendarColorData,
    ICalendarColorResponse,
    QueryStringTrialData,
    IPaymentsResponse,
    ICalendarProjectsResponse,
    IGetUserTaxEntitiesResponseSuccess,
    IDefaultAutoExpirationResponse,
    IAcceptedTOSVersionResponse,
    IAcceptTOSResponse,
    IBaseSetAuthCodeRequest,
    IBaseSetAuthCodeWithValidationCodeRequest,
    IResetByGuidRequest,
    ISetActiveEmailProviderRequest,
    ISetActiveEmailProviderResponse,
    ISetGoogleAuthCodeRequest,
    ISetGoogleAuthCodeResponse,
    ISetNylasAuthCodeRequest,
    ISetQuickbooksAuthCodeRequest,
    ISetStepCompletePayload,
    ISetStepCompleteResponse,
    IStartPhoneVerificationType
} from '~/types/server.types';
import {
    AccountRequestActions,
    TBookkeeperAccessActions
} from '~/models/account-access-request.model';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import {
    UiPersistence,
    IChangePasswordByEmailParams
} from '~/types/angular.types';
import {
    ICreateCalendarItemRequest,
    ICreateCalendarItemResponse,
    IGetCalendarItemResponse,
    IGetCalendarItemsResponse
} from '~/types/calendar-item.types';
import { TMethod2FA } from '~/types/enums.types';
import { getForterToken } from '~/utils/forter-load.utils';
import { IGetIntegratedCalendarEventsForTimeRangeResponse } from '~/types/calendar.types';
import { CalendarPopoverType } from '~/apps/main/Calendar/utils/calendar-page.types';
import { TDefaultAutoExpirationOption } from '~/apps/main/CompanySettings/CompanyPreferences/server-types';
import { IImage, IQuickbooksIntegration } from '~/types/model.types';
import { IReferralCampaign } from '~/components/Referral/components/ReferralsPage/types';
import { IClientPortalMagicLinkParams } from '~/common/useClientPortalMagicLink';
import { ITaxDocument } from '~/components/TINVerification/types';

export interface LoginWithGoogleFirebaseParams extends QueryStringTrialData {
    auth_token?: string;
    id_token?: string;
    sso_type: string;
}

export interface SendResetPasswordUrlParams extends QueryStringTrialData {
    'g-recaptcha-response-v3'?: string;
    'g-recaptcha-response'?: string;
    client_portal_company_id?: string;
    is_widget?: boolean;
    fingerprint?: string;
}

export interface ConvertPreTrailerData {
    full_name: string;
    company_name: string;
    company_type: string;
    phone_number: string;
    password?: string;
    _id?: string;
}

export class UserAdapter extends BaseHttpAdapter {
    async getUser(id: string) {
        const response = await this.apiService.apiRequest<IUserResponse>({
            method: 'get',
            url: v2GetCurrentUserPath(id)
        });

        return response;
    }

    async _getCampaigns(page: TCampaignPage) {
        return this.apiService.apiRequest<ICampaign[]>({
            method: 'get',
            url: `/api/v2/campaigns?page=${page}`
        });
    }

    async getFullstoryToken() {
        const response =
            await this.apiService.apiRequest<IFullstoryTokenResponse>({
                method: 'get',
                url: '/api/v2/users/fullstory_token'
            });

        return response;
    }

    async fetchOnboardingSequence(userId: string) {
        return this.apiService.apiRequest<IOnboardingSequence>({
            url: v2OnboardingSequencePath(userId)
        });
    }

    async validateEmailIntakeForm(userData, fingerprintHeader) {
        return this.apiService.apiRequest<IValidateEmailIntakeForm>({
            url: validateOnboardingPath(userData),
            headers: fingerprintHeader
        });
    }

    async testEmailDeliverability() {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: '/api/v2/email-deliverability-tests'
        });
    }

    async fetchEmailDeliverabilityTestStatus() {
        return this.apiService.apiRequest<IEmailDeliverabilityTestResponse>({
            method: 'get',
            url: '/api/v2/email-deliverability-tests/last'
        });
    }

    async _loginWithGoogleFirebase(requestData: LoginWithGoogleFirebaseParams) {
        return this.apiService.apiRequest({
            method: 'post',
            url: '/api/v2/users/oauth/sso_signup_user',
            data: requestData
        });
    }

    async _createNewUser(userData, headers) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'post',
            url: '/api/v2/users',
            data: userData,
            headers
        });
    }

    async requestAccessFromUser(memberEmail: string, teamMemberIds: string[]) {
        return this.apiService.apiRequest<any>({
            method: 'PUT',
            data: { email: memberEmail, team_members_ids: teamMemberIds },
            url: `/api/v2/pro-authorizations`
        });
    }

    async _getProRequests(presentParams: string[]) {
        return this.apiService.apiRequest<IProAccessResponse>({
            method: 'get',
            url: '/api/v2/pro-authorizations?' + presentParams.join('&')
        });
    }

    async _getBookkeeperRequests(asClient: boolean) {
        return this.apiService.apiRequest<IBookkeeperAccessResponse>({
            method: 'get',
            url: `/api/v2/bookkeeper-authorizations?as_client=${asClient}`
        });
    }

    async startPro2FA() {
        return this.apiService.apiRequest({
            method: 'post',
            url: '/api/v2/users/pro/start-mfa'
        });
    }

    async _proLoginAsVendor(user_id: string, smsCode: string) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'post',
            data: { sms_code: smsCode },
            url: `/api/v2/users/pro/login/${user_id}`
        });
    }

    async manageProAccountAccess(
        requestId: string,
        action: AccountRequestActions
    ) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: `/api/v2/pro-authorizations/${requestId}/${action}`
        });
    }

    async manageBookkeeperAccountAccess(
        requestId: string,
        action: TBookkeeperAccessActions
    ) {
        return this.apiService.apiRequest<any>({
            method: 'post',
            url: `/api/v2/bookkeeper-authorizations/${requestId}/${action}`
        });
    }

    async saveBookkeeperUser(data: IInviteBookkeeperDataForServer) {
        return this.apiService.apiRequest<IAccessRequestResponse>({
            method: 'post',
            url: `/api/v2/bookkeeper/${data.userId}/invite`,
            data
        });
    }

    async updatePhoneNumberForTwoAuth({
        phoneNumber
    }: {
        phoneNumber: string;
    }) {
        const data = {
            phone_number: phoneNumber,
            type: 'sms'
        };

        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/users/save_phone_number_and_start_2fa`,
            data
        });
    }

    async addUserTags(userId: string, options?: object) {
        return this.apiService.apiRequest<any>({
            method: 'put',
            url: v2UserTagsPath(userId, options)
        });
    }

    updateCompanyForMWebQuiz(
        userId: string,
        { companyType, companyName, website }
    ) {
        return this.apiService.apiRequest<any>({
            method: 'put',
            url: `/api/v2/users/${userId}/update_company_for_mweb_quiz`,
            params: {
                company_type: companyType,
                company_name: companyName,
                website_url: website
            }
        });
    }

    _getContacts(email: string, currUserId: string) {
        return this.apiService.apiRequest<any>({
            method: 'get',
            url: `/api/v2/user/${currUserId}/contacts`,
            params: { exact_email: true, text: email }
        });
    }

    validateEmail(email: string) {
        return this.apiService.apiRequest({
            method: 'get',
            url: `/api/v2/validate/email/${email}`
        });
    }

    setUiPersistence<T extends keyof UiPersistence>(
        userId: string,
        data: { key: T; value: UiPersistence[T] }
    ) {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/users/${userId}/ui_persistence`,
            data
        });
    }

    fetchGoogleContacts(
        userId: string,
        params: {
            search_term: string;
            filter_existing_contacts: boolean;
            max_results: number;
            start_index: number;
        }
    ) {
        return this.apiService.apiRequest<IGoogleContactsResponse>({
            method: 'get',
            url: `/api/v2/users/${userId}/google_contacts`,
            params
        });
    }

    addClientToWorkspace(data: ISaveContactRequest, workspace_id: string) {
        return this.apiService.apiRequest<ICreateEventWithClientResponseUser>({
            method: 'post',
            url: v2GetWorkspaceMembersPath(workspace_id, data)
        });
    }

    async createClientAndAddToWorkspace(
        data: ISaveContactRequest,
        workspace_id: string
    ) {
        return this.apiService.apiRequest<ISaveContactResponse>({
            method: 'post',
            url: `/api/v2/workspaces/${workspace_id}/clients`,
            data
        });
    }

    async addExistingClientToWorkspace(email: string, workspace_id: string) {
        return this.apiService.apiRequest<ISaveContactResponse>({
            method: 'post',
            url: `/api/v2/workspaces/${workspace_id}/clients/${email}`
        });
    }

    async addCollaboratorToWorkspace(
        data: ISaveContactRequest,
        workspace_id: string
    ) {
        return this.apiService.apiRequest<ISaveContactResponse>({
            method: 'post',
            url: `/api/v2/workspaces/${workspace_id}/members`,
            data: {
                ...data
            }
        });
    }

    editContact(data: IEditContactRequest) {
        return this.apiService.apiRequest<ISaveContactResponse>({
            method: 'post',
            url: `/api/v2/contact`,
            data
        });
    }

    getZapierApiKey(userId: string) {
        return this.apiService.apiRequest<{ api_key: string }>({
            method: 'get',
            url: v2UserGetZapierApiKeyPath(userId)
        });
    }

    getZoomAuthURL(userId: string, returnURL: string) {
        return this.apiService.apiRequest<{ zoom_auth_url: string }>({
            method: 'get',
            url: `/api/v2/users/${userId}/zoom_auth_url`,
            params: { return_url: returnURL }
        });
    }

    getZoomAuthProvider(userId: string) {
        return this.apiService.apiRequest<IZoomAuthProviderResponse>({
            method: 'get',
            url: `/api/v2/users/${userId}/zoom_auth_provider`,
            networkLabel: 'getZoomAuthProvider'
        });
    }

    disconnectZoom(userId: string) {
        return this.apiService.apiRequest<IZoomAuthProviderResponse>({
            method: 'delete',
            url: `/api/v2/users/${userId}/zoom`
        });
    }

    getCanvaAuthURL(userId: string, returnURL: string) {
        return this.apiService.apiRequest<{ canva_auth_url: string }>({
            method: 'get',
            url: `api/v2/users/${userId}/canva_auth_url`,
            params: { return_url: returnURL }
        });
    }

    getCanvaAuthProvider(userId: string) {
        return this.apiService.apiRequest<ICanvaAuthProviderResponse>({
            method: 'get',
            url: `api/v2/users/${userId}/canva_auth_provider`
        });
    }

    disconnectCanva(userId: string) {
        return this.apiService.apiRequest<ICanvaAuthProviderResponse>({
            method: 'delete',
            url: `api/v2/users/${userId}/canva`
        });
    }

    getCalendlyAuthUrl(returnUrl: string, currUserId: string) {
        return this.apiService.apiRequest<any>({
            method: 'get',
            url: `/api/v2/users/${currUserId}/calendly_auth_url`,
            params: { return_url: returnUrl }
        });
    }

    disconnectCalendlyPath(currUserId: string) {
        return this.apiService.apiRequest<any>({
            method: 'delete',
            url: `/api/v2/users/${currUserId}/calendly`
        });
    }

    requestNewIntegration(
        currUserId: string,
        data: { name: string; reason: string }
    ) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/users/${currUserId}/request_new_integration`,
            data
        });
    }

    getCalendarProvider(userId: string) {
        return this.apiService.apiRequest<ICalendarProvidersResponse>({
            method: 'get',
            url: `/api/v2/users/${userId}/calendar_provider`,
            networkLabel: 'getCalendarProvider'
        });
    }

    async deleteCalendarProvider(userId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/users/${userId}/calendar_provider`,
            networkLabel: 'deleteCalendarProvider'
        });
    }

    createCalendarItem(userId: string, data: ICreateCalendarItemRequest) {
        return this.apiService.apiRequest<ICreateCalendarItemResponse>({
            method: 'post',
            url: v2CreateCalendarItemPath(userId),
            data
        });
    }

    updateCalendarItem(
        userId: string,
        calendarItemId: string,
        data: Partial<ICreateCalendarItemRequest>
    ) {
        return this.apiService.apiRequest<ICreateCalendarItemResponse>({
            method: 'put',
            url: v2UpdateCalendarItemPath(userId, calendarItemId),
            data
        });
    }

    getGoogleAuthURL(currUserId: string, data: IGetGoogleAuthURLRequest) {
        return this.apiService.apiRequest<{ google_auth_url: string }>({
            method: 'get',
            url: `/api/v2/users/${currUserId}/google_auth_url`,
            params: data
        });
    }

    async setGoogleAuthCode({
        currUserId,
        authCode,
        integrationType,
        validationCode
    }: ISetGoogleAuthCodeRequest): Promise<ISetGoogleAuthCodeResponse> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_google_auth_code`,
            params: {
                auth_code: authCode,
                integration_type: integrationType,
                validation_code: validationCode
            }
        });
    }

    async setCalendlyAuthCode({
        currUserId,
        authCode,
        validationCode
    }: IBaseSetAuthCodeWithValidationCodeRequest): Promise<void> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_calendly_auth_code`,
            params: {
                auth_code: authCode,
                validation_code: validationCode
            }
        });
    }

    async setZoomAuthCode({
        currUserId,
        authCode,
        validationCode
    }: IBaseSetAuthCodeWithValidationCodeRequest): Promise<void> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_zoom_auth_code`,
            params: {
                auth_code: authCode,
                validation_code: validationCode
            }
        });
    }

    async setCanvaAuthCode({
        currUserId,
        authCode
    }: IBaseSetAuthCodeRequest): Promise<void> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_canva_auth_code`,
            params: {
                auth_code: authCode
            }
        });
    }

    async setNylasAuthCode({
        currUserId,
        authCode,
        stateString
    }: ISetNylasAuthCodeRequest): Promise<void> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_nylas_auth_code`,
            data: {
                data: {
                    code: authCode,
                    state: stateString
                }
            }
        });
    }

    async setYahooAuthCode({
        currUserId,
        authCode
    }: IBaseSetAuthCodeRequest): Promise<void> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/users/${currUserId}/set_nylas_auth_code`,
            data: {
                data: {
                    code: authCode,
                    provider: 'yahoo'
                }
            }
        });
    }

    async setQuickbooksAuthCode({
        authCode,
        stateString,
        realmId
    }: ISetQuickbooksAuthCodeRequest): Promise<{
        quickbooks_integration: Omit<IQuickbooksIntegration, 'items'>;
    }> {
        return this.apiService.apiRequest({
            method: 'POST',
            url: `/api/v2/quickbooks/auth_code`,
            data: {
                state: stateString,
                code: authCode,
                realmId
            }
        });
    }

    connectSocialAccount({
        userId,
        authToken,
        redirectUri,
        socialNetworkType
    }: {
        userId: string;
        authToken: string;
        redirectUri?: string;
        socialNetworkType: Extract<SocialNetwork, 'facebook' | 'instagram'>;
    }) {
        return this.apiService.apiRequest<
            Pick<IUserResponse, 'social_network_accounts'>
        >({
            method: 'put',
            url: v2UserConnectSocialAccountPath(userId),
            data: {
                auth_token: authToken,
                redirect_uri: redirectUri,
                social_network_type: socialNetworkType
            }
        });
    }

    disconnectSocialAccount({
        userId,
        socialNetworkType
    }: {
        userId: string;
        socialNetworkType: Extract<SocialNetwork, 'facebook' | 'instagram'>;
    }) {
        return this.apiService.apiRequest<
            Pick<IUserResponse, 'social_network_accounts'>
        >({
            method: 'put',
            url: v2UserDisconnectSocialAccountPath(userId),
            data: { social_network_type: socialNetworkType }
        });
    }

    getNylasAuthCode(currUserId: string, data: IGetNylasAuthURLRequest) {
        return this.apiService.apiRequest<{ nylas_auth_url: string }>({
            method: 'get',
            url: `/api/v2/users/${currUserId}/nylas_auth_url`,
            params: data
        });
    }

    updateCalendarProvider(
        currUserId: string,
        settings: IUpdateCalendarProviderRequest
    ) {
        return this.apiService.apiRequest<ICalendarProvidersResponse>({
            method: 'put',
            url: `/api/v2/users/${currUserId}/calendar_provider`,
            data: settings
        });
    }

    getEmailProvider(currUserId: string) {
        return this.apiService.apiRequest<IEmailProvidersResponse>({
            method: 'get',
            url: v2GetEmailProvidersPath(currUserId)
        });
    }

    disconnectEmailProvider(currUserId: string, provider: TEmailProvider) {
        return this.apiService.apiRequest<IEmailProvidersResponse>({
            method: 'delete',
            url: v2DeleteEmailProviderPath(currUserId, provider)
        });
    }

    async endOoo(userId: string): Promise<void> {
        return this.apiService.apiRequest<void>({
            method: 'put',
            url: `/api/v2/users/${userId}/end_out_of_office`
        });
    }

    async cancelPendingTeamMemberInvite(companyId: string, inviteId: string) {
        await this.apiService.apiRequest({
            url: `/api/v2/user/${companyId}/cancel_pending_invite`,
            method: 'PUT',
            data: {
                invite_id: inviteId
            }
        });
    }

    getCalendarEventsOnDate(
        currUserId: string,
        params: { date: string | null; end_date: string | null }
    ): Promise<IGetCalendarEventsOnDateResponse> {
        return this.apiService.apiRequest<IGetCalendarEventsOnDateResponse>({
            method: 'get',
            url: v2GetCalendarEventsOnDatePath(currUserId, params)
        });
    }

    async sendLogin2FA(
        method: TMethod2FA
    ): Promise<ISendLogin2FAResponseSuccess> {
        return this.apiService.apiRequest({
            method: 'post',
            url: '/api/v2/users/send-login-2fa',
            data: { type: method }
        });
    }

    async validate2FACode(
        code: string,
        ownIdSessionToken: string
    ): Promise<IValidate2FACodeResponseSuccess> {
        return this.apiService.apiRequest({
            method: 'post',
            url: '/api/v2/users/validate-login-2fa',
            data: {
                code,
                f_token: getForterToken(),
                own_id_session_token: ownIdSessionToken
            },
            networkLabel: 'validate2FACode'
        });
    }

    async update2FAState(
        enabled: boolean
    ): Promise<IUpdate2FAStateResponseSuccess> {
        return this.apiService.apiRequest({
            method: 'post',
            url: '/api/v2/users/update-login-2fa-state',
            data: { enabled },
            networkLabel: 'update2FAState'
        });
    }

    async changePassword(
        userId: string,
        data: IChangePasswordData
    ): Promise<TChangePasswordResponse> {
        return this.apiService.apiRequest({
            method: 'put',
            url: v2UsersChangePasswordPath(userId),
            data
        });
    }

    async updateUserEmail(
        userId: string,
        email: string,
        verificationCode: string
    ): Promise<void> {
        return this.apiService.apiRequest({
            method: 'put',
            url: v2UserUpdateUserEmailPath(userId),
            data: { email, sms_code: verificationCode },
            networkLabel: 'updateUserEmail'
        });
    }

    updateUser(userId: string, data: Partial<IUserResponse>) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'put',
            url: `/api/v2/users/${userId}`,
            data
        });
    }

    updateUserProfilePicture(userId: string, data: Partial<IImage>) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'post',
            url: `/api/v2/users/${userId}/photo/profile_image`,
            data
        });
    }

    async sendEmailVerification(userId: string) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/users/${userId}/send_email_verification`,
            data: {
                type_link: 'email_verification'
            },
            networkLabel: 'sendEmailVerification'
        });
    }

    async startPhoneVerification({
        userId,
        type
    }: IStartPhoneVerificationType): Promise<void> {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: `/api/v2/users/${userId}/phone_verification`,
            data: { type },
            networkLabel: 'sendEmail2faVerification'
        });
    }

    async setActiveEmailProvider(
        userId: string,
        data: ISetActiveEmailProviderRequest
    ): Promise<ISetActiveEmailProviderResponse> {
        return this.apiService.apiRequest<ISetActiveEmailProviderResponse>({
            method: 'put',
            url: `/api/v2/users/${userId}/email_provider_type`,
            data
        });
    }

    async sendPasswordLink(userId: string): Promise<TSendPasswordLinkResponse> {
        return this.apiService.apiRequest({
            url: v2UsersSendPasswordLinkPath(userId),
            method: 'post',
            networkLabel: 'sendPasswordLink'
        });
    }

    async updatePhoneNumberForTwoFactorAuth(
        userId: string,
        data: IUpdatePhoneNumberForTwoFactorAuthData
    ): Promise<TUpdatePhoneNumberForTwoFactorAuthResponse> {
        return this.apiService.apiRequest({
            url: v2UserUpdatePhoneNumberForTwoFactorAuthenticationPath(userId),
            data,
            method: 'put',
            networkLabel: 'updatePhoneNumberForTwoFactorAuth'
        });
    }

    async sendResetPasswordLink(
        email: string,
        params: SendResetPasswordUrlParams,
        headers?: Record<string, string>
    ) {
        return this.apiService.apiRequest<void>({
            method: 'post',
            url: '/api/v2/users/reset_link',
            data: { email, ...params },
            headers
        });
    }

    async updateCalendarColor(userId: string, data: IUpdateCalendarColorData) {
        return this.apiService.apiRequest<ICalendarColorResponse>({
            url: `/api/v2/users/${userId}/change_calendar_color`,
            method: 'put',
            data
        });
    }

    async getIntegratedCalendarsList(userId: string) {
        return this.apiService.apiRequest<IIntegratedCalendar[]>({
            url: `/api/v2/users/${userId}/calendar_provider/calendar_importer/origin_calendars_list`,
            method: 'get',
            networkLabel: 'getIntegratedCalendarsList'
        });
    }

    async getCalendarItems(
        userId: string,
        from_date?: Date | string,
        to_date?: Date | string
    ) {
        return this.apiService.apiRequest<IGetCalendarItemsResponse>({
            url: `/api/v2/users/${userId}/calendar_items`,
            method: 'get',
            params: { from_date, to_date },
            networkLabel: 'getCalendarItems'
        });
    }

    async getCalendarItem(userId: string, calendarItemId: string) {
        return this.apiService.apiRequest<IGetCalendarItemResponse>({
            url: `/api/v2/users/${userId}/calendar_items/${calendarItemId}`,
            method: 'get',
            networkLabel: 'getCalendarItem'
        });
    }

    async deleteCalendarItem(
        userId: string,
        calendarItemId: string,
        notifyMembers?: boolean
    ) {
        return this.apiService.apiRequest<void>({
            url: `/api/v2/users/${userId}/calendar_items/${calendarItemId}`,
            method: 'delete',
            params: { notify: !!notifyMembers },
            networkLabel: 'deleteCalendarItem'
        });
    }

    async reSyncCalendarItem(
        userId: string,
        calendarItemId: string,
        eventType: CalendarPopoverType
    ) {
        return this.apiService.apiRequest({
            url: `/api/v2/users/${userId}/calendar_items/${calendarItemId}/resync`,
            method: 'post',
            data: { eventType },
            networkLabel: 'reSyncCalendarItem'
        });
    }

    async getIntegratedCalendarEventsForTimeRange(
        userId: string,
        fromDate: string,
        toDate: string
    ) {
        return this.apiService.apiRequest<IGetIntegratedCalendarEventsForTimeRangeResponse>(
            {
                url: `/api/v2/users/${userId}/calendar_provider/calendar_importer/events/${fromDate}/${toDate}`,
                method: 'get',
                networkLabel: 'getIntegratedCalendarEventsForTimeRange'
            }
        );
    }

    async getPayments(
        userId: string,
        isMinimal: boolean,
        fromDate: string,
        toDate: string
    ) {
        return this.apiService.apiRequest<IPaymentsResponse>({
            url: `/api/v2/reporting/users/${userId}/reports/payments`,
            method: 'get',
            params: {
                minimal_response: isMinimal,
                from_date: fromDate,
                to_date: toDate
            },
            networkLabel: 'getPayments'
        });
    }

    async getCalendarProjects(
        userId: string,
        fromDate: string,
        toDate: string
    ) {
        return this.apiService.apiRequest<ICalendarProjectsResponse>({
            url: `/api/v2/reporting/users/${userId}/reports/calendar`,
            method: 'get',
            params: {
                from_date: fromDate,
                to_date: toDate
            },
            networkLabel: 'getReportsCalendar'
        });
    }

    async getCurrUserTaxEntities(): Promise<IGetUserTaxEntitiesResponseSuccess> {
        return this.apiService.apiRequest({
            url: `/api/v2/users/tax_entities`,
            method: 'get',
            networkLabel: 'getTaxEntities'
        });
    }

    async getPdfStatus(params: { request_id: string }) {
        return this.apiService.apiRequest<{
            template_id?: string;
            instance_id?: string;
            error?: any;
        }>({
            url: '/api/v2/flow_from_pdf/websocket_fallback',
            method: 'get',
            params
        });
    }

    async setCalendarImporter(userId: string) {
        return this.apiService.apiRequest<ICalendarProvidersResponse>({
            url: `/api/v2/users/${userId}/calendar_provider/calendar_importer`,
            method: 'post',
            networkLabel: 'setCalendarImporter'
        });
    }

    async setNylasCalendarImporter(userId: string) {
        return this.apiService.apiRequest<ICalendarProvidersResponse>({
            url: `/api/v2/users/${userId}/calendar_provider/set_calendar_importer`,
            method: 'post',
            networkLabel: 'setNylasCalendarImporter'
        });
    }

    async sendClientPortalMagicLink(data: IClientPortalMagicLinkParams) {
        return this.apiService.apiRequest<void>({
            url: `/api/v2/users/loginless_client_portal_link`,
            method: 'post',
            data
        });
    }

    async updateAutoExpireDefault(
        userId: string,
        period: TDefaultAutoExpirationOption
    ) {
        return this.apiService.apiRequest<IDefaultAutoExpirationResponse>({
            url: `/api/v2/users/${userId}/default_auto_expiration`,
            method: 'put',
            data: {
                default_auto_expiration_period: period
            }
        });
    }

    async deleteAutoExpirationDefault(userId: string) {
        return this.apiService.apiRequest<IDefaultAutoExpirationResponse>({
            url: `/api/v2/users/${userId}/default_auto_expiration`,
            method: 'delete'
        });
    }

    async getAtwsData(userId: string) {
        return this.apiService.apiRequest<{
            atws_status: IUserResponse['atws_status'];
            onbording_container: IUserResponse['onboarding_container'];
        }>({
            url: `/api/v2/users/${userId}/atws`,
            method: 'get'
        });
    }

    async generateJwt() {
        return this.apiService.apiRequest<{ token: string }>({
            url: `/api/v2/user/generate_jwt`,
            method: 'get',
            params: {
                fields_to_encode: ['user_id']
            }
        });
    }

    async clearLastInstantPayoutError(userId: string, paymentMethodId: string) {
        return this.apiService.apiRequest({
            url: `/api/v2/users/${userId}/bank_account/${paymentMethodId}`,
            method: 'put',
            data: { clear_last_instant_deposit_fail_reason: true }
        });
    }

    async setInstantDepositToBankAccountStatus(
        userId: string,
        paymentMethodId: string,
        enabled: boolean
    ) {
        return this.apiService.apiRequest({
            url: `/api/v2/users/${userId}/bank_account/${paymentMethodId}`,
            method: 'put',
            data: { enable_instant_deposit_to_bank_account: enabled }
        });
    }

    async addWebpnEndpoint(userId: string, subscription: PushSubscription) {
        return this.apiService.apiRequest<void>({
            url: `/api/v2/users/${userId}/add_webpn`,
            method: 'post',
            data: { subscription }
        });
    }

    async removeWebpnEndpoint(userId: string, subscription: PushSubscription) {
        return this.apiService.apiRequest<void>({
            url: `/api/v2/users/${userId}/remove_webpn`,
            method: 'delete',
            data: { endpoint: subscription.endpoint }
        });
    }

    async setCalendarImport(
        userId: string,
        calendarImportId: string,
        enabled: boolean
    ) {
        return this.apiService.apiRequest({
            url: `/api/v2/users/${userId}/calendar_provider/calendar_importer/calendar_import/${calendarImportId}`,
            method: 'put',
            data: { enabled }
        });
    }

    async getAcceptedTOSVersion(userId: string) {
        return this.apiService.apiRequest<IAcceptedTOSVersionResponse | null>({
            url: `/api/v2/users/${userId}/unaccepted_tos`,
            method: 'get'
        });
    }

    async acceptTOSVersion(userId: string) {
        return this.apiService.apiRequest<IAcceptTOSResponse>({
            url: `/api/v2/users/${userId}/accept_terms_of_service`,
            method: 'put'
        });
    }

    async unauthenticatedChangePassword(
        data: IChangePasswordByEmailParams & { company_id?: string }
    ) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'put',
            url: '/api/v2/users/password_by_email',
            data: {
                email: data.email,
                current_password: data.current_password,
                new_password: data.new_password,
                company_id: data.company_id,
                'g-recaptcha-response-v3': data['g-recaptcha-response-v3']
            }
        });
    }

    async getReferralCampaignDetails(userId: string) {
        return this.apiService.apiRequest<IReferralCampaign>({
            url: '/api/v2/referrals/campaign_details',
            params: { user_id: userId }
        });
    }

    async resetUserPassword(
        userId: string,
        guid: string,
        password: string,
        fingerprint: string
    ) {
        const data = {
            id: userId,
            password,
            reset_password_guid: guid,
            fingerprint
        };

        return this.apiService.apiRequest<IUserResponse>({
            method: 'put',
            url: '/api/v2/users/reset_password',
            data,
            networkLabel: 'resetUserPassword'
        });
    }

    async getUserByResetGuid({
        userId,
        guid,
        fileId,
        eventId
    }: IResetByGuidRequest) {
        const params: {
            id: string;
            reset_password_guid: string;
            file_id?: string;
            event_id?: string;
        } = {
            id: userId,
            reset_password_guid: guid
        };

        if (fileId) {
            params.file_id = fileId;
        }
        if (eventId) {
            params.event_id = eventId;
        }

        return this.apiService.apiRequest<IUserResponse>({
            method: 'get',
            url: '/api/v2/users/by_reset_guid',
            params
        });
    }

    async getCampaignDetails(userId: string) {
        return this.apiService.apiRequest<IReferralCampaign>({
            url: '/api/v2/referrals/campaign_details',
            params: { user_id: userId }
        });
    }

    async _convertPreTrailerToTrailer(
        userId: string,
        conversionData: ConvertPreTrailerData
    ) {
        return this.apiService.apiRequest<IUserResponse>({
            method: 'post',
            url: v2UserConvertPreTrailerToTrailerPath(userId),
            data: conversionData
        });
    }

    async setStepComplete(
        userId: string,
        stepName: string,
        payload: ISetStepCompletePayload
    ) {
        return this.apiService.apiRequest<ISetStepCompleteResponse>({
            method: 'put',
            url: `/api/v2/users/${userId}/self_onboarding/steps/${stepName}`,
            data: payload
        });
    }

    async getUserCommunityToken() {
        return this.apiService.apiRequest<{
            token: string;
            redirect_url: string;
        }>({
            method: 'get',
            url: `/api/v2/users/community_token`
        });
    }

    async getTaxDocuments(userId: string) {
        return this.apiService.apiRequest<ITaxDocument[]>({
            url: '/api/v2/users/taxes/documents',
            params: { user_id: userId }
        });
    }

    async createEmailSignature(userId: string, data: unknown = null) {
        return this.apiService.apiRequest<unknown>({
            method: 'post',
            url: '/api/v2/users/templates/email/signature',
            data
        });
    }

    async getEmailSignature(emailSignatureId: string) {
        return this.apiService.apiRequest<unknown>({
            method: 'get',
            url: `/api/v2/users/templates/email/signature/${emailSignatureId}`
        });
    }

    async deleteEmailSignature(emailSignatureId: string) {
        return this.apiService.apiRequest<void>({
            method: 'delete',
            url: `/api/v2/users/templates/email/signature/${emailSignatureId}`
        });
    }
}
